import { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto'
import type { Revision, SchoolType } from '@mathflat/domain/@entities/SchoolSystem/schema'
import { makeAutoObservable } from 'mobx'

type Params = {
  includeAll?: boolean
  expandedLabel?: string
  includePreOpen?: boolean
  revisions?: Revision[]
}

const defaultParams = {
  includeAll: true,
  includePreOpen: true,
  revisions: ['CURRICULUM_15', 'CURRICULUM_22'],
} as const satisfies Params

export class CurriculumService {
  protected _curriculumMap: Map<CurriculumEntity.TrieKey, CurriculumEntity.CurriculumCoreItem> =
    new Map()

  constructor(curriculum: CurriculumEntity.CurriculumCore) {
    makeAutoObservable(this)

    const { grades, semesters, subjects } = curriculum

    grades.forEach((grade) => {
      this._curriculumMap.set(grade.trieKey, grade)
    })

    semesters.forEach((semester) => {
      this._curriculumMap.set(semester.trieKey, semester)
    })

    subjects.forEach((subject) => {
      this._curriculumMap.set(subject.trieKey, subject)
    })
  }

  private get _gradeSubjects(): Record<SchoolType, CurriculumEntity.CurriculumCoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && !item.semester && item.status !== 'HIDDEN',
      ),
      MIDDLE: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && !item.semester && item.status !== 'HIDDEN',
      ),
      HIGH: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH' && item.status !== 'HIDDEN',
      ),
    }
  }

  get allGrade() {
    return [
      ...this._gradeSubjects.ELEMENTARY,
      ...this._gradeSubjects.MIDDLE,
      ...this._gradeSubjects.HIGH,
    ]
  }

  private get _gradeSemesterSubjects(): Record<SchoolType, CurriculumEntity.CurriculumCoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && item.semester && item.status !== 'HIDDEN',
      ),
      MIDDLE: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && item.semester && item.status !== 'HIDDEN',
      ),
      HIGH: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH' && item.status !== 'HIDDEN',
      ),
    }
  }

  private _filterByRevision = (targetRevision: Revision, revisions: Revision[]) => {
    return revisions.includes(targetRevision)
  }

  private _filterByPreOpenInclude = (
    includePreOpen: boolean,
    item: CurriculumEntity.CurriculumCoreItem,
  ) => {
    if (includePreOpen) return true
    return item.status !== 'PRE_OPEN'
  }

  private _getAllTypeItem({
    schoolType,
    expandedLabel,
  }: {
    schoolType?: SchoolType
    expandedLabel?: string
  }): CurriculumEntity.AllTypeCurriculumCoreItem<SchoolType | null> | undefined {
    if (schoolType) {
      return new CurriculumEntity.AllTypeCurriculumCoreItem<SchoolType>({
        schoolType,
        label: expandedLabel,
      })
    } else {
      return new CurriculumEntity.AllTypeCurriculumCoreItem<null>({
        schoolType: null,
        label: expandedLabel,
      })
    }
  }

  private _getCurriculumArray({
    type = 'GRADE',
    includeAll = defaultParams.includeAll,
    includePreOpen = defaultParams.includePreOpen,
    revisions = defaultParams.revisions,
    expandedLabel,
    schoolType = 'ELEMENTARY',
  }: Params & { schoolType: SchoolType; type: 'GRADE' | 'SEMESTER' }) {
    let _gradeSubject: CurriculumEntity.CurriculumCoreItem[] = []
    switch (schoolType) {
      case 'ELEMENTARY':
        _gradeSubject =
          type === 'SEMESTER'
            ? this._gradeSemesterSubjects.ELEMENTARY
            : this._gradeSubjects.ELEMENTARY
        break
      case 'MIDDLE':
        _gradeSubject =
          type === 'SEMESTER' ? this._gradeSemesterSubjects.MIDDLE : this._gradeSubjects.MIDDLE
        break
      case 'HIGH':
        _gradeSubject = this._gradeSubjects.HIGH
        break
    }

    _gradeSubject =
      _gradeSubject
        .filter((item) => this._filterByRevision(item.revision || 'CURRICULUM_15', revisions))
        .filter((item) => this._filterByPreOpenInclude(includePreOpen, item)) || []

    if (includeAll) {
      const ALL_TYPE_ITEM = this._getAllTypeItem({ schoolType, expandedLabel })
      return ALL_TYPE_ITEM ? [ALL_TYPE_ITEM, ..._gradeSubject] : _gradeSubject
    } else {
      return _gradeSubject
    }
  }
  get allGradeSemester() {
    return [
      ...this.getGradeSemesterElementary({ includeAll: false }),
      ...this.getGradeSemesterMiddle({ includeAll: false }),
      ...this.getGradeSubjectHigh({ includeAll: false }),
    ]
  }
  // 초등학교 ======================================================================================================
  /** 초등학교 학년-학기 */
  getGradeSemesterElementary<T extends boolean>(
    params: Params & { includeAll?: T } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'ELEMENTARY',
      type: 'SEMESTER',
    }) as T extends true
      ? (
          | CurriculumEntity.AllTypeCurriculumCoreItem<'ELEMENTARY'>
          | CurriculumEntity.CurriculumCoreItem<'ELEMENTARY'>
        )[]
      : CurriculumEntity.CurriculumCoreItem<'ELEMENTARY'>[]
  }

  // 중학교 ======================================================================================================
  /** 중학교 학년 - 학기 */
  getGradeSemesterMiddle<T extends boolean>(
    params: Params & { includeAll?: T } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'MIDDLE',
      type: 'SEMESTER',
    }) as T extends true
      ? (
          | CurriculumEntity.AllTypeCurriculumCoreItem<'MIDDLE'>
          | CurriculumEntity.CurriculumCoreItem<'MIDDLE'>
        )[]
      : CurriculumEntity.CurriculumCoreItem<'MIDDLE'>[]
  }

  // 고등학교 ======================================================================================================
  /** 고등학교 과목 */
  getGradeSubjectHigh<T extends boolean>(
    params: Params & { includeAll?: T } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'HIGH',
      type: 'GRADE',
    }) as T extends true
      ? (
          | CurriculumEntity.AllTypeCurriculumCoreItem<'HIGH'>
          | CurriculumEntity.CurriculumCoreItem<'HIGH'>
        )[]
      : CurriculumEntity.CurriculumCoreItem<'HIGH'>[]
  }

  // 공통 ======================================================================================================
  getItemByTrieKey(trieKey: CurriculumEntity.TrieKey) {
    if (trieKey === '') {
      console.error('trieKey is empty')
    }
    if (trieKey === 'ALL') {
      console.error('trieKey is ALL : 사용부에서 All check 필요')
    }
    return this._curriculumMap.get(trieKey)
  }
}
